import React, { useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import logo from "../../assets/images/log03.png";
import mizan from "../../assets/images/sign-mizan.png";
import shahin from "../../assets/images/sign-sahin.png";
import { calculateGreat, calculateMyPosition, handleGreat, maxMark, totalMarks } from "../../Utils/CalculateFunction";
import { generateStudentComment } from "../../Utils/CommentGenerator";

const ProgressReport = ({ 
  student, 
  examData, 
  xmSub, 
  classe, 
  examName, 
  classStatistics, 
  studentPosition 
}) => {
  // Calculate student data
  const myResult = examData?.Results?.find((i) => i?.studentPh === student?.phoneNumber);

  const no_mcq_pr = ["Play", "Nursery", "One", "Two", "Three", "Four", "Five"].includes(student?.class)
  

  // console.log(xmSub, "xmSub");
  
  // Filter subjects for Class Nine and Ten (Agricultural vs Higher Math)
  const filteredResult = useMemo(() => {
    if (!myResult) return null;
    
    if (student?.class === "Nine" || student?.class === "Ten") {
      const hasAgricultural = myResult?.subResult?.find(s => s?.subject === "Agricultural");
      const hasHigherMath = myResult?.subResult?.find(s => s?.subject === "Higher Math");
      
      if (hasAgricultural && hasHigherMath) {
        // If both exist, keep only Higher Math
        return { ...myResult, subResult: myResult?.subResult?.filter(s => s?.subject !== "Agricultural") };
      } else if (hasHigherMath && !hasAgricultural) {
        // If only Higher Math exists, keep it
        return myResult;
      } else if (hasAgricultural && !hasHigherMath) {
        // If only Agricultural exists, keep it
        return myResult;
      }
    }
    
    return myResult;
  }, [myResult, student?.class]);



  // Filter xmSub array to show only relevant subjects
  const filteredXmSub = useMemo(() => {
    if (student?.class === "Nine" || student?.class === "Ten") {
      const hasAgricultural = myResult?.subResult?.find(s => s?.subject === "Agricultural");
      const hasHigherMath = myResult?.subResult?.find(s => s?.subject === "Higher Math");
      
      if (hasAgricultural && hasHigherMath) {
        // If both exist, remove Agricultural from xmSub
        return xmSub?.filter(subject => subject?.name !== "Agricultural");
      } else if (hasHigherMath && !hasAgricultural) {
        // If only Higher Math exists, remove Higher Math from xmSub
        return xmSub?.filter(subject => subject?.name !== "Agricultural");
      } else if (hasAgricultural && !hasHigherMath) {
        // If only Agricultural exists, remove Higher Math from xmSub
        return xmSub?.filter(subject => subject?.name !== "Higher Math");
      }
    }
    
    return xmSub;
  }, [xmSub, myResult, student?.class]);


  // console.log(filteredXmSub, "filteredXmSub");


  const maxTotalMarks2 = filteredXmSub?.reduce((total, sub) => {
    return total + (sub?.maxNum > 100 ? 100 : sub?.maxNum);
  }, 0);
  
  // Helper functions
  const marks = (sub) => {
    if (!filteredResult) return null;
    const sumMark = filteredResult?.subResult?.find((i) => i?.subject === sub);
    return sumMark;
  };

  const STR = (i) => {
    if (!i) return 0;
    const m = i?.mcq ? i?.mcq : 0;
    const w = i?.writing ? i?.writing : 0;
    const p = i?.pecticNun ? i?.pecticNun : 0;
    const mark = parseFloat(m) + parseFloat(w) + parseFloat(p);
    return mark;
  };

  const calculateGPA = (marks) => {
    if (!marks || !filteredResult) return [0, "F"];

    const gradePoints = marks?.map((x) => {
      const crnSub = filteredXmSub?.find((i) => i?.name === x?.subMa?.subject);
      if (crnSub?.name === "Higher Math" || crnSub?.name === "Agricultural") {
        const optionalSub =
          x?.mark >= 80
            ? 3
            : x?.mark >= 70
            ? 2
            : x?.mark >= 60
            ? 1.5
            : x?.mark >= 50
            ? 1
            : 0;
        return optionalSub;
      } else if (x?.mark >= Math.ceil((crnSub?.maxNum / 100) * 80)) {
        return 5;
      } else if (x?.mark >= Math.ceil((crnSub?.maxNum / 100) * 70)) {
        return 4;
      } else if (x?.mark >= Math.ceil((crnSub?.maxNum / 100) * 60)) {
        return 3.5;
      } else if (x?.mark >= Math.ceil((crnSub?.maxNum / 100) * 50)) {
        return 3;
      } else if (x?.mark >= Math.ceil((crnSub?.maxNum / 100) * 40)) {
        return 2;
      } else if (x?.mark >= Math.ceil((crnSub?.maxNum / 100) * 33)) {
        return 1;
      } else {
        return 0;
      }
    });

    const great = (gpa) => {
      if (gpa >= 5) {
        return "A+";
      } else if (gpa >= 4) {
        return "A";
      } else if (gpa >= 3.5) {
        return "A-";
      } else if (gpa >= 3) {
        return "B";
      } else if (gpa >= 2) {
        return "C";
      } else if (gpa >= 1) {
        return "D";
      } else {
        return "F";
      }
    };



    const totalGradePoints = gradePoints?.reduce((acc, curr) => acc + curr, 0);
    const subLength = filteredXmSub?.length > 11 ? 11 : filteredXmSub?.length;
    const gpa = totalGradePoints / subLength;
    // console.log(totalGradePoints, gradePoints, "gradePoints", gpa, subLength);
    return [gpa > 5 ? Math.floor(gpa).toFixed(2) : gpa.toFixed(2), great(gpa)];
  };

  const result = filteredResult?.subResult?.map((i) => {
    const SubMark = {
      mark: STR(i),
      subMa: i,
    };
    return SubMark;
  });


  const grtGPA = calculateGPA(result);
  const year = new Date().getFullYear()?.toString().slice(2);
  const studentId = student?.phoneNumber?.slice(10) + year + student?.roleNo;

  // Enhanced calculations with memoization
  const studentCalculations = useMemo(() => {
    if (!filteredResult) return null;

    const totalMarks = filteredResult?.subResult?.reduce((total, subject) => {
      return total + STR(subject);
    }, 0);

    const maxTotalMarks = filteredXmSub?.reduce((total, sub) => {
      return total + (sub?.maxNum > 100 ? 100 : sub?.maxNum);
    }, 0);

    // Calculate grade and status
    const { myGreats, status, rejected } = calculateGreat(filteredResult, filteredXmSub);
    
    // Calculate highest marks in each subject
    const subjectHighestMarks = filteredXmSub?.map(subject => {
      if (!classStatistics?.results) return 0;
      
      const subjectMarks = classStatistics.results.map(r => {
        const subResult = r.result?.subResult?.find(s => s?.subject === subject?.name);
        return subResult ? STR(subResult) : 0;
      });
      
      return Math.max(...subjectMarks, 0);
    });

    return {
      totalMarks,
      maxTotalMarks : maxTotalMarks,
      myGreats,
      status,
      rejected,
      subjectHighestMarks,
      percentage: maxTotalMarks > 0 ? (totalMarks / maxTotalMarks) * 100 : 0
    };
  }, [filteredResult, filteredXmSub, classStatistics]);

  const totalMarks = studentCalculations?.totalMarks || 0;
  const maxTotalMarks = studentCalculations?.maxTotalMarks || maxTotalMarks2;
  const { myGreats, status, rejected } = studentCalculations || { myGreats: [], status: 'FAIL', rejected: 0 };
  const subjectHighestMarks = studentCalculations?.subjectHighestMarks || [];
  const percentage = studentCalculations?.percentage || 0;

  // Generate dynamic comment based on student performance
  const studentComment = useMemo(() => {
    return generateStudentComment({
      position: studentPosition || 0,
      totalStudents: classStatistics?.totalStudents || 1,
      percentage: percentage,
      gpa: parseFloat(grtGPA[0]) || 0,
      studentName: student?.firstName || 'Student'
    });
  }, [studentPosition, classStatistics?.totalStudents, percentage, grtGPA, student?.firstName]);

  // Generate unique random icons for each cell
  const getRandomIcons = useMemo(() => {
    const allIcons = ['✅', '✓', '◐', '○', '✗', '★', '●', '◆', '▲', '■', '♦', '♠', '♥', '♣', '◉', '◈', '◊', '◌', '◍', '◎'];
    
    // Use student ID or phone number as seed for consistent randomness per student
    const seed = student?.phoneNumber?.slice(-4) || '1234';
    const seedNumber = parseInt(seed) % 1000;
    
    // Generate unique random index for each cell
    const getRandomIcon = (multiplier) => {
      const index = (seedNumber * multiplier) % allIcons.length;
      return allIcons[index];
    };
    
    return {
      // Moral & Behavior Evaluation icons
      excellentMoral: getRandomIcon(3),
      goodMoral: getRandomIcon(5),
      averageMoral: getRandomIcon(7),
      poorMoral: getRandomIcon(11),
      
      // Co-Curricular Activities icons
      sports: getRandomIcon(13),
      cultural: getRandomIcon(17),
      scout: getRandomIcon(19),
      mathOlympiad: getRandomIcon(23)
    };
  }, [student?.phoneNumber]);

  return (
    <div className="w-full font-Oswald">
        <div
                  className="absolute top-0 left-0 w-full h-[267mm]"
                  style={{
                    backgroundImage: `url(${logo})`,
                    backgroundSize: '500px', // Adjust size as per requirement
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    opacity: 0.1, // Subtle watermark
                    // zIndex: 1,
                  }}
                ></div>
      {/* Header Section */}
      <div className="flex justify-between w-full items-start ">
        {/* Left Side - School Info */}
        <div className="flex items-center justify-between space-x-2">
          <div className="w-[110px] h-[110px] shrink-0 rounded-full flex items-center justify-center">
            <img src={logo} alt="Logo" className="w-[110px] h-[110px] " />
          </div>
        </div>
          <div className='text-center w-full font-reem'>
            <h1 className="text-5xl font-bold text-blue-600 font-Oswald mb-1">Nabojagoron Academy</h1>
            <p className="text-[20px] text-gray-600 mt-3">Nanni, Nalitabari, Sherpur.</p>
            <p className="text-2xl font-semibold">Progress Report-{new Date().getFullYear()}.</p>
          </div>

       {/* Student Photo Placeholder */}
      <div className=" w-24 h-24 border-4 shrink-0 border-blue-600 rounded-full overflow-hidden flex items-center justify-center bg-gray-100">
        {student?.imageURL ? (
          <img src={student?.imageURL} alt="Student" className="w-24 h-24 object-cover" />
        ) : (
          <span className="text-4xl font-bold font-reem text-blue-600">
            {student?.firstName?.charAt(0)}{student?.lastName?.charAt(0)}
          </span>
        )}
      </div>
      </div>

      <div className="border-b-[3px] border-dashed border-blue-500 w-16 my-4 mx-auto"></div>

        {/* Student Information */}
        <div className="flex justify-between gap-x-4 items-start">
          <div className="w-full">
            <table className="w-full text-[16px] leading-[23px]">
              <tbody>
                <tr>
                  <td className="border border-gray-400 px-3 py-1 font-medium bg-gray-100 w-32">Student Name:</td>
                  <td className="border border-gray-400 px-3 py-2   uppercase" colSpan="3">{student?.firstName} {student?.lastName}</td>
                  
                </tr>
                <tr>
                <td className="border border-gray-400 px-3 py-1 font-medium bg-gray-100 w-32">Father's Name:</td>
                <td className="border border-gray-400 px-3 py-1" colSpan="3">{student?.fatherName || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-3 py-1 font-medium bg-gray-100">Mother's Name:</td>
                  <td className="border border-gray-400 px-3 py-1" colSpan="3">{student?.motherName || 'N/A'}</td>
                  
                </tr>
                <tr>
                  <td className="border border-gray-400 px-3 py-1 font-medium bg-gray-100">Class:</td>
                  <td className="border border-gray-400 px-3 py-1">{student?.class}</td>
                  <td className="border border-gray-400 px-3 py-1 font-medium bg-gray-100">Roll No:</td>
                  <td className="border border-gray-400 px-3 py-1">{student?.roleNo}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-3 py-1 font-medium bg-gray-100">Group:</td>
                  <td className="border border-gray-400 px-3 py-1">{no_mcq_pr ? "N/A" : "Science"}</td>
                  <td className="border border-gray-400 px-3 py-1 font-medium bg-gray-100">Gender:</td>
                  <td className="border border-gray-400 px-3 py-1 capitalize">{student?.gender}</td>
                </tr>
                <tr>
                <td className="border border-gray-400 px-3 py-1 font-medium bg-gray-100">Student ID:</td>
                <td className="border border-gray-400 px-3 py-1">{studentId}</td>
                  <td className="border border-gray-400 px-3 py-1 font-medium bg-gray-100">Reg:</td>
                  <td className="border border-gray-400 px-3 py-1" >{student?.phoneNumber?.slice(5)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        <div className="text-sm">
          <table className="border border-gray-400">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-400 px-2 py-0.5 min-w-[60px] font-medium">Range</th>
                <th className="border border-gray-400 px-2 py-0.5 font-medium">Grade</th>
                <th className="border border-gray-400 px-2 py-0.5 font-medium">GPA</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="border border-gray-400 px-2 py-0.5">80-100</td><td className="border border-gray-400 px-2 py-0.5 text-center">A+</td><td className="border border-gray-400 px-2 py-0.5">5.0</td></tr>
              <tr><td className="border border-gray-400 px-2 py-0.5">70-79</td><td className="border border-gray-400 px-2 py-0.5 text-center">A</td><td className="border border-gray-400 px-2 py-0.5">4.0</td></tr>
              <tr><td className="border border-gray-400 px-2 py-0.5">60-69</td><td className="border border-gray-400 px-2 py-0.5 text-center">A-</td><td className="border border-gray-400 px-2 py-0.5">3.5</td></tr>
              <tr><td className="border border-gray-400 px-2 py-0.5">50-59</td><td className="border border-gray-400 px-2 py-0.5 text-center">B</td><td className="border border-gray-400 px-2 py-0.5">3.0</td></tr>
              <tr><td className="border border-gray-400 px-2 py-0.5">40-49</td><td className="border border-gray-400 px-2 py-0.5 text-center">C</td><td className="border border-gray-400 px-2 py-0.5">2.0</td></tr>
              <tr><td className="border border-gray-400 px-2 py-0.5">33-39</td><td className="border border-gray-400 px-2 py-0.5 text-center">D</td><td className="border border-gray-400 px-2 py-0.5">1.0</td></tr>
              <tr><td className="border border-gray-400 px-2 py-0.5">0-32</td><td className="border border-gray-400 px-2 py-0.5 text-center">F</td><td className="border border-gray-400 px-2 py-0.5">0.0</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="border-b-[3px] border-dashed border-blue-500 w-16 my-4 mx-auto"></div>

        {/* Examination Results Table */}
        <div className="">
          {/* <h3 className="text-center text-lg font-bold text-blue-600 mb-2">2nd Semester Examination</h3> */}
          <table className="w-full leading-[22px] text-sm">
          <thead>
            <tr><th className="border border-gray-400 px-2 text-center text-xl font-bold text-blue-600 bg-gray-100 font-reem" colSpan="9">{examName}</th></tr>
            <tr className="bg-gray-100">
              <th className="border border-gray-400 px-2 py-0.5 font-medium text-left">Subject</th>
              <th className="border border-gray-400 px-2 py-0.5 font-medium text-center">Full Marks</th>
              <th className="border border-gray-400 px-2 py-0.5 font-medium text-center">Highest marks</th>
              <th className="border border-gray-400 px-2 py-0.5 font-medium text-center">Writing</th>
              {!no_mcq_pr && <th className="border border-gray-400 px-2 py-0.5 font-medium text-center">MCQ</th>}
              {!no_mcq_pr && <th className="border border-gray-400 px-2 py-0.5 font-medium text-center">PR</th>}
              <th className="border border-gray-400 px-2 py-0.5 font-medium text-center">Total Marks</th>
              <th className="border border-gray-400 px-2 py-0.5 font-medium text-center">Letter Grade</th>
              <th className="border border-gray-400 px-2 py-0.5 font-medium text-center">Grade Point</th>
            </tr>
          </thead>
          <tbody>
            {filteredXmSub?.map((subject, index) => {
              const subjectMarks = marks(subject?.name);
              const totalSubjectMarks = STR(subjectMarks);
              const maxMarks = subject?.maxNum > 100 ? 100 : subject?.maxNum;
              const highestMarks = subjectHighestMarks[index] || 0;
              const pairedSubjects = ["Bangla 1st", "Bangla 2nd", "English 1st", "English 2nd"];
              
              // Check if this is the first subject of a pair
              const isFirstOfPair = (subject?.name === "Bangla 1st" || subject?.name === "English 1st");
              
              // Calculate combined grade for paired subjects
              const getCombinedGrade = (subjectName) => {
                if (subjectName === "Bangla 1st") {
                  const bangla1stMarks = marks("Bangla 1st");
                  const bangla2ndMarks = marks("Bangla 2nd");
                  const total1st = STR(bangla1stMarks);
                  const total2nd = STR(bangla2ndMarks);
                  const combinedTotal = total1st + total2nd;
                  
                  const bangla1stSub = filteredXmSub?.find(s => s.name === "Bangla 1st");
                  const bangla2ndSub = filteredXmSub?.find(s => s.name === "Bangla 2nd");
                  const max1st = bangla1stSub?.maxNum > 100 ? 100 : bangla1stSub?.maxNum || 0;
                  const max2nd = bangla2ndSub?.maxNum > 100 ? 100 : bangla2ndSub?.maxNum || 0;
                  const combinedMax = max1st + max2nd;
                  
                  if (combinedMax === 0) return { grade: 'F', point: 0.0 };
                  
                  const percentage = (combinedTotal / combinedMax) * 100;
                  if (percentage >= 80) return { grade: 'A+', point: 5.0 };
                  if (percentage >= 70) return { grade: 'A', point: 4.0 };
                  if (percentage >= 60) return { grade: 'A-', point: 3.5 };
                  if (percentage >= 50) return { grade: 'B', point: 3.0 };
                  if (percentage >= 40) return { grade: 'C', point: 2.0 };
                  if (percentage >= 33) return { grade: 'D', point: 1.0 };
                  return { grade: 'F', point: 0.0 };
                }
                
                if (subjectName === "English 1st") {
                  const english1stMarks = marks("English 1st");
                  const english2ndMarks = marks("English 2nd");
                  const total1st = STR(english1stMarks);
                  const total2nd = STR(english2ndMarks);
                  const combinedTotal = total1st + total2nd;
                  
                  const english1stSub = filteredXmSub?.find(s => s.name === "English 1st");
                  const english2ndSub = filteredXmSub?.find(s => s.name === "English 2nd");
                  const max1st = english1stSub?.maxNum > 100 ? 100 : english1stSub?.maxNum || 0;
                  const max2nd = english2ndSub?.maxNum > 100 ? 100 : english2ndSub?.maxNum || 0;
                  const combinedMax = max1st + max2nd;
                  
                  if (combinedMax === 0) return { grade: 'F', point: 0.0 };
                  
                  const percentage = (combinedTotal / combinedMax) * 100;
                  if (percentage >= 80) return { grade: 'A+', point: 5.0 };
                  if (percentage >= 70) return { grade: 'A', point: 4.0 };
                  if (percentage >= 60) return { grade: 'A-', point: 3.5 };
                  if (percentage >= 50) return { grade: 'B', point: 3.0 };
                  if (percentage >= 40) return { grade: 'C', point: 2.0 };
                  if (percentage >= 33) return { grade: 'D', point: 1.0 };
                  return { grade: 'F', point: 0.0 };
                }
                
                return null;
              };
              
              const combinedGrade = isFirstOfPair ? getCombinedGrade(subject?.name) : null;

               const crnSub = filteredXmSub?.find((i) => i?.name === subject?.name);
              
              return (
                <tr key={index}>
                  <td className="border border-gray-400 px-2 py-0.5">{subject?.name === "Islam" ? "Religion" : subject?.name}</td>
                  <td className="border border-gray-400 px-2 py-0.5 text-center">{maxMarks}</td>
                  <td className="border border-gray-400 px-2 py-0.5 text-center">{highestMarks || '-'}</td>
                  <td className="border border-gray-400 px-2 py-0.5 text-center">{subjectMarks?.writing || '-'}</td>
                  {!no_mcq_pr && <td className="border border-gray-400 px-2 py-0.5 text-center">{subjectMarks?.mcq || '-'}</td>}
                  {!no_mcq_pr && <td className="border border-gray-400 px-2 py-0.5 text-center">{subjectMarks?.pecticNun || '-'}</td>}
                  <td className="border border-gray-400 px-2 py-0.5 text-center">{totalSubjectMarks || '-'}</td>
                  
                  {/* Letter Grade Column */}
                  {isFirstOfPair ? (
                    <td className="border border-gray-400 px-2 py-0.5 text-center" rowSpan="2">
                      {combinedGrade?.grade || '-'}
                    </td>
                  ) : !pairedSubjects.includes(subject?.name) ? (
                    <td className="border border-gray-400 px-2 py-0.5 text-center">
                      {handleGreat(totalSubjectMarks, filteredXmSub, subject?.name, subjectMarks)}
                    </td>
                  ) : null}
                  
                  {/* Grade Point Column */}
                  {isFirstOfPair ? (
                    <td className="border border-gray-400 px-2 py-0.5 text-center" rowSpan="2">
                      {combinedGrade?.point ? combinedGrade.point.toFixed(1) : '0.0'}
                    </td>
                  ) : !pairedSubjects.includes(subject?.name) ? (
                    <td className="border border-gray-400 px-2 py-0.5 text-center">
                      {totalSubjectMarks >=  Math.ceil((crnSub?.maxNum / 100) * 80) ? '5.0' : 
                       totalSubjectMarks >=  Math.ceil((crnSub?.maxNum / 100) * 70) ? '4.0' : 
                       totalSubjectMarks >=  Math.ceil((crnSub?.maxNum / 100) * 60) ? '3.5' : 
                       totalSubjectMarks >=  Math.ceil((crnSub?.maxNum / 100) * 50) ? '3.0' : 
                       totalSubjectMarks >=  Math.ceil((crnSub?.maxNum / 100) * 40) ? '2.0' : 
                       totalSubjectMarks >=  Math.ceil((crnSub?.maxNum / 100) * 33) ? '1.0' : '0.0'}
                    </td>
                  ) : null}
                </tr>
              );
            })}
            <tr className="font-medium">
              <td className="border border-gray-400 px-2 py-0.5">Total Marks: </td>
              <td className="border border-gray-400 px-2 py-0.5 text-center">{maxTotalMarks}</td>
              <td className="border border-gray-400 px-2 py-0.5 text-center" colSpan={no_mcq_pr ? 2 : 4}>Obtained Marks & GPA</td>
              {/* <td className="border border-gray-400 px-2 py-0.5"></td>
              <td className="border border-gray-400 px-2 py-0.5"></td>
              <td className="border border-gray-400 px-2 py-0.5"></td> */}
              <td className="border border-gray-400 px-2 py-0.5 text-center">{totalMarks || '-'}</td>
              <td className="border border-gray-400 px-2 py-0.5 text-center">
              <span className={`${status === "FAIL" ? "line-through text-gray-400" : ""}`}>{grtGPA[1]}</span> {' '} {status === "FAIL" && <span className=""> F</span>}
              </td>
              <td className="border border-gray-400 px-2 py-0.5 text-center"><span className={`${status === "FAIL" ? "line-through text-gray-400" : ""}`}>{ grtGPA[0]}</span> {' '} {status === "FAIL" && <span className="text-red-500">0.00</span>} </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="border-b-[3px] border-dashed border-blue-500 w-16 my-4 mx-auto"></div>
        {/* Summary Section */}
        <div className="flex justify-between gap-4 text-sm">
          {/* Result Status */}
          <div className="shrink-0">
            <table className="w-full text-xs">
              <tbody>
                <tr>
                  <td className="border bg-gray-100 border-gray-400 px-1 py-0.5 text-center text-[14px] leading-[20px] font-medium">Result Status: </td>
                  <td className="border bg-gray-100 border-gray-400 px-1 py-0.5 text-center font-medium w-[60px]"><span className={`${status === 'PASS' ? 'text-green-500' : 'text-red-500'} text-[14px]`}>{status === 'PASS' ? 'Passed' : 'Failed'}</span></td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-0.5 font-medium w-[120px]">Class Position:</td>
                  <td className="border border-gray-400 px-1 py-0.5 text-center">
                    {studentPosition > 0 ? `${studentPosition} of ${classStatistics?.totalStudents || 0}` : '-'}
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-0.5 font-medium">GPA (Without 4th):</td>
                  <td className="border border-gray-400 px-1 py-0.5 text-center">{status === "FAIL" ? "0.00" : grtGPA[0]}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-0.5 font-medium">Failed Subject(s):</td>
                  <td className="border border-gray-400 px-1 py-0.5 text-center">{rejected || 0}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-0.5 font-medium">Percentage:</td>
                  <td className="border border-gray-400 px-1 py-0.5 text-center">{percentage.toFixed(1)}%</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-0.5 font-medium">Total Present:</td>
                  <td className="border border-gray-400 px-1 py-0.5 text-center">-</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-0.5 font-medium">Total Absent:</td>
                  <td className="border border-gray-400 px-1 py-0.5 text-center">-</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Moral & Behavior Evaluation */}
          <div className="w-full">
            <table className="w-full text-xs">
              <tbody>
                <tr>
                  <td className="border bg-gray-100 border-gray-400 px-1 py-1 font-medium text-center" colSpan="2">Moral & Behavior Evaluation</td>
                  <td className="border bg-gray-100 border-gray-400 px-1 py-1 font-medium text-center" colSpan="2">Co-Curricular Activities</td>
                  <td className="border bg-gray-100 border-gray-400 px-1 py-1 font-medium text-center" colSpan="2">QR CODE</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-1 font-medium">Excellent:</td>
                  <td className="border border-gray-400 px-1 py-1 text-center w-[60px]">{getRandomIcons.excellentMoral}</td>
                  <td className="border border-gray-400 px-1 py-1 font-medium">Sports:</td>
                  <td className="border border-gray-400 px-1 py-1 text-center w-[60px]">{getRandomIcons.sports}</td>
                  <td className="border w-[90px] border-gray-400 px-1 py-1 text-center" rowSpan="4">
                  <div className="w-[90px] h-[90px] flex items-center justify-center mx-auto">
                  <QRCodeSVG
                    value={
                      `https://nabojagoron.com/`
                    }
                    size={80}
                    level="H"
                  />
            </div>
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-1 font-medium">Good:</td>
                  <td className="border border-gray-400 px-1 py-1 text-center">{getRandomIcons.goodMoral}</td>
                  <td className="border border-gray-400 px-1 py-1 font-medium">Cultural Function:</td>
                  <td className="border border-gray-400 px-1 py-1 text-center">{getRandomIcons.cultural}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-1 font-medium">Average:</td>
                  <td className="border border-gray-400 px-1 py-1 text-center">{getRandomIcons.averageMoral}</td>
                  <td className="border border-gray-400 px-1 py-1 font-medium">Scout/BNCC:</td>
                  <td className="border border-gray-400 px-1 py-1 text-center">{getRandomIcons.scout}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-1 font-medium">Poor:</td>
                  <td className="border border-gray-400 px-1 py-1 text-center">{getRandomIcons.poorMoral}</td>
                  <td className="border border-gray-400 px-1 py-1 font-medium">Math Olympiad:</td>
                  <td className="border border-gray-400 px-1 py-1 text-center">{getRandomIcons.mathOlympiad}</td>
                </tr>
                <tr>
                  <td className="border border-gray-400 px-1 py-1 font-medium" colSpan="5"> Comments: <span className="text-xs font-normal">{studentComment}</span></td>
                </tr>
             
              </tbody>
            </table>
          </div>

        

       
        </div>

        {/* Signatures */}
        <div className="absolute bottom-[-20px] font-reem left-0 w-full">
          <table className="w-full  text-sm">
            <tbody>
              <tr>
                <td className="border border-t-0 border-gray-400 px-4 pb-2 text-center w-1/3">
                  <div className="border-b border-dashed border-gray-400 w-24 h-6 mb-2 mx-auto"></div>
                  <span className="font-medium text-blue-600"></span>
                  <span className="font-bold">Class Teacher</span>
                </td>
                <td className="border border-t-0 border-gray-400 px-4 pb-2 text-center w-1/3">
                  <div className=" w-24 h-6 mb-1 mx-auto">
                    <img src={shahin} alt="shahin" className="w-full h-full" />
                  </div>
                  <span className="font-bold text-blue-600">Md. Shahin Alam</span>
                  <br />
                  <span className="">Exam Controller</span>
                </td>
                <td className="border border-t-0 border-gray-400 px-4 pb-2 text-center w-1/3">
                  <div className=" w-24 h-6 mb-1 mx-auto">
                    <img src={mizan} alt="mizan" className="w-full h-full" />
                  </div>
                  <span className="font-bold text-blue-600 ">Md.Mizanur Rahman</span>
                  <br />
                  <span className="">Principal.</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
    </div>
  );
};

export default ProgressReport;
